import { Box, IconButton, Theme, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Icon } from '@/components/Icon/Icon';
import { PageWithRightImage } from '@/components/Layout/Page/PageWithRightImage.tsx';
import { useUpdatePersonalInfo } from '@/components/PageForm/PersonalDetails';
import { UpdatePersonalInfo } from '@/components/PageForm/PersonalDetails/Update/UpdatePersonalInfo';

import { ROUTE } from '@/constants/routes';

import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { getDataTestId } from '@/utils/browser';

import { useReviewPersonalDetails } from './hooks/useReviewPersonalDetails';

export default function UpdatePersonalInfoPage() {
  const { t } = useTranslate();
  const { isMobile } = useResponsive();

  const navigate = useNavigate();
  const { onContinue, isPending: isConfirmPending } =
    useReviewPersonalDetails();
  const title = t('remediation.updatePersonalDetails.title');

  const {
    methods,
    onConfirm,
    onSubmit,
    isPending: isUpdatePending,
    isValid,
    isOpenConfirmPopup,
    closeConfirmPopup,
    resetForm,
  } = useUpdatePersonalInfo({
    triggerFormValidation: true,
    onSuccess: onContinue,
  });

  const onCancel = () => {
    resetForm(undefined);
    navigate(ROUTE.REVIEW_PERSONAL_DETAILS, { replace: true });
  };

  return (
    <PageWithRightImage
      loading={isUpdatePending || isConfirmPending}
      bgColor={theme => theme.palette.neutral[100]}
      headerMobile={{
        leftSlot: (
          <IconButton
            aria-label={t('common.close')}
            onClick={onCancel}
            {...getDataTestId('update-personal-info-page-close-button')}
          >
            <Icon
              name="close"
              color={(theme: Theme) => theme.palette.neutral[700]}
            />
          </IconButton>
        ),
        centerSlot: (
          <Typography variant="headlineH5" textAlign="center">
            {title}
          </Typography>
        ),
        bgColor: (theme: Theme) => theme.palette.neutral[100],
      }}
    >
      <Box
        sx={{
          paddingTop: { xs: 1, lg: 0 },
          paddingBottom: { xs: 4, lg: 0 },
          width: '100%',
        }}
      >
        {!isMobile && (
          <Typography variant="headlineH2" mb={3}>
            {title}
          </Typography>
        )}
        <UpdatePersonalInfo
          methods={methods}
          onConfirm={onConfirm}
          onSubmit={onSubmit}
          isPending={isUpdatePending || isConfirmPending}
          isValid={isValid}
          isOpenConfirmPopup={isOpenConfirmPopup}
          closeConfirmPopup={closeConfirmPopup}
          onCancel={isMobile ? undefined : onCancel}
        />
      </Box>
    </PageWithRightImage>
  );
}
